import * as React from "react";
import { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-perfect-scrollbar/dist/css/styles.css";
import "react-image-lightbox/style.css";
import "react-notifications-component/dist/theme.css";
import "animate.css/animate.min.css";
import "nprogress/nprogress.css";
import "./App.css";
import "./AppCustom.css";

import { ReactNotifications } from "react-notifications-component";
import {
  BestExpressTrackingStatus,
  FlashTrackingStatus,
  JntTrackingStatus,
  KerryTrackingStatus,
  SpxExpressTrackingStatus,
  ThaiPostTrackingStatus,
} from "./common/config/app";

import DomainAuth from "./components/admin/DomainAuth";
import PageDenied from "./components/PageDenied";
import NProgress from "nprogress";
import { useSelector } from "react-redux";
import { userConfig } from "./features/reducer/user/userSlice";
import { decryptJson } from "./common/functions/encrypt";

const LazyLoad = () => {
  useEffect(() => {
    NProgress.start();
    return () => {
      NProgress.done();
    };
  });

  return "";
};

/*Auth*/
const Login = React.lazy(() => import("./components/authentication/Login"));
const Logout = React.lazy(() => import("./components/authentication/Logout"));

const Admin = React.lazy(() => import("./components/admin/Admin"));
const Dashboard = React.lazy(() =>
  import("./components/admin/dashboard/Dashboard")
);
const Product = React.lazy(() => import("./components/admin/product/Product"));
const ProductInOut = React.lazy(() =>
  import("./components/admin/product/ProductInOut")
);
const Category = React.lazy(() =>
  import("./components/admin/category/Category")
);

/*ORDER*/
const OrderOpenManual = React.lazy(() =>
  import("./components/admin/order/OrderOpenManual")
);
const Order = React.lazy(() => import("./components/admin/order/Order"));
const OrderOpen = React.lazy(() =>
  import("./components/admin/order/OrderOpen")
);
const OrderView = React.lazy(() =>
  import("./components/admin/order/OrderView")
);
const OrderImport = React.lazy(() =>
  import("./components/admin/order/OrderImport")
);
const OrderTeleSale = React.lazy(() =>
  import("./components/admin/order/OrderTeleSale")
);

/*Shipping*/
const JntOrder = React.lazy(() =>
  import("./components/admin/shipping/jnt/JntOrder")
);

const ThaipostOrder = React.lazy(() =>
  import("./components/admin/shipping/thaipost/ThaipostOrder")
);
const LaosOrders = React.lazy(() =>
  import("./components/admin/shipping/laos/LaosOrders")
);
const KerryOrder = React.lazy(() =>
  import("./components/admin/shipping/kerry/KerryOrder")
);
const FlashOrder = React.lazy(() =>
  import("./components/admin/shipping/flash/FlashOrder")
);
const BestExpressOrder = React.lazy(() =>
  import("./components/admin/shipping/bestexpress/BestExpressOrder")
);
const SpxOrder = React.lazy(() =>
  import("./components/admin/shipping/spx/SpxOrder")
);

const POD = React.lazy(() => import("./components/admin/shipping/flash/POD"));
const FlashExpressNotification = React.lazy(() =>
  import("./components/admin/shipping/flash/FlashExpressNotification")
);
const FlashPrintHistory = React.lazy(() =>
  import("./components/admin/shipping/flash/FlashPrintHistory")
);
const CodChecker = React.lazy(() =>
  import("./components/admin/shipping/CodChecker")
);
const CodReport = React.lazy(() =>
  import("./components/admin/shipping/flash/CodReport")
);

/*OTHER*/
const Expense = React.lazy(() => import("./components/admin/expense/Expense"));

/*REPORT*/
const ReportMonthy = React.lazy(() =>
  import("./components/admin/report/ReportMonthy")
);
const ReportCommission = React.lazy(() =>
  import("./components/admin/report/ReportCommission")
);
const ReportOrder = React.lazy(() =>
  import("./components/admin/report/ReportOrder")
);
const ReportShipping = React.lazy(() =>
  import("./components/admin/report/ReportShipping")
);
const ReportDaily = React.lazy(() =>
  import("./components/admin/report/ReportDaily")
);
const ReportProduct = React.lazy(() =>
  import("./components/admin/report/ReportProduct")
);
const ReportLeadNotification = React.lazy(() =>
  import("./components/admin/report/ReportLeadNotification")
);

const ReportCustomer = React.lazy(() =>
  import("./components/admin/report/ReportCustomer")
);

/*User*/
const User = React.lazy(() => import("./components/admin/user/User"));
const UserCreate = React.lazy(() =>
  import("./components/admin/user/UserCreate")
);
const UserProfile = React.lazy(() =>
  import("./components/admin/user/UserProfile")
);

/*Setting*/
const AddressKerry = React.lazy(() =>
  import("./components/admin/setting/AddressKerry")
);
const AddressThaipost = React.lazy(() =>
  import("./components/admin/setting/AddressThaipost")
);
const Address = React.lazy(() => import("./components/admin/address/Address"));
const SettingGeneral = React.lazy(() =>
  import("./components/admin/setting/SettingGeneral")
);
const Channels = React.lazy(() => import("./components/admin/channel/Channel"));

/*CRM*/
const PiplineCreate = React.lazy(() =>
  import("./components/admin/crm/pipeline/PiplineCreate")
);
const Pipline = React.lazy(() =>
  import("./components/admin/crm/pipeline/Pipeline")
);
const LeadList = React.lazy(() =>
  import("./components/admin/crm/lead/LeadLists")
);
const LeadListCancel = React.lazy(() =>
  import("./components/admin/crm/lead/LeadListsCancel")
);
const LeadCreate = React.lazy(() =>
  import("./components/admin/crm/lead/LeadCreate")
);
const LeadManage = React.lazy(() =>
  import("./components/admin/crm/lead/LeadManage")
);
const LeadFollow = React.lazy(() =>
  import("./components/admin/crm/lead/LeadFollows")
);
const LeadImport = React.lazy(() =>
  import("./components/admin/crm/lead/LeadImport")
);
const LeadCalendar = React.lazy(() =>
  import("./components/admin/crm/calendar/Calendar")
);

function App() {
  const uConfig = useSelector(userConfig);
  const [config, setConfig] = useState(uConfig);

  useEffect(() => {
    if (uConfig !== null && uConfig !== ``) {
      setConfig(decryptJson(uConfig));
    }
  }, [uConfig]);

  return (
    <>
      <Router>
        <React.Suspense fallback={<LazyLoad />}>
          <Routes>
            <Route exact={true} path="/login" element={<Login />} />
            <Route exact={true} path="/logout" element={<Logout />} />
            <Route path="/" element={<Admin />}>
              {/*<Route path="/container" element={<Container/>}/>*/}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/" element={<Dashboard />} />
              {/*<Route path="/" element={<Order status="send"/>}/>*/}

              <Route
                path="/crm/pipeline"
                element={
                  <DomainAuth shipping="crm">
                    <Pipline />
                  </DomainAuth>
                }
              />

              <Route
                path="/crm/lead/create"
                element={
                  <DomainAuth shipping="crm">
                    <LeadCreate />
                  </DomainAuth>
                }
              />
              <Route
                path="/crm/lead/list"
                element={
                  <DomainAuth shipping="crm">
                    <LeadList />
                  </DomainAuth>
                }
              />
              <Route
                path="/crm/lead/cancel"
                element={
                  <DomainAuth shipping="crm">
                    <LeadListCancel />
                  </DomainAuth>
                }
              />
              <Route
                path="/crm/lead/manage/:id"
                element={
                  <DomainAuth shipping="crm">
                    <LeadManage />
                  </DomainAuth>
                }
              />
              <Route
                path="/crm/lead/follow"
                element={
                  <DomainAuth shipping="crm">
                    <LeadFollow />
                  </DomainAuth>
                }
              />
              <Route
                path="/crm/lead/import"
                element={
                  <DomainAuth shipping="crm">
                    <LeadImport />
                  </DomainAuth>
                }
              />
              <Route
                path="/crm/lead/calendar"
                element={
                  <DomainAuth shipping="crm">
                    <LeadCalendar />
                  </DomainAuth>
                }
              />

              <Route path="/category" element={<Category />} />
              <Route path="/product" element={<Product />} />
              <Route path="/product/stock" element={<ProductInOut />} />

              <Route
                exact
                path="/expense/facebook"
                element={<Expense type="facebook" />}
              />
              <Route
                exact
                path="/expense/shipping"
                element={<Expense type="shipping" />}
              />
              <Route
                exact
                path="/expense/other"
                element={<Expense type="other" />}
              />

              <Route path="/order/import" element={<OrderImport />} />
              {/*<Route path="/order/telesale" element={<OrderTeleSale/>}/>*/}
              <Route
                path="/order/telesale"
                element={<Order status="telesale" />}
              />
              <Route path="/order/open" element={<OrderOpen />} />
              <Route path="/order/create" element={<OrderOpenManual />} />
              <Route path="/order/:id/view" element={<OrderView />} />
              <Route path="/order/send" element={<Order status="send" />} />
              <Route
                path="/order/pending"
                element={<Order status="pending" />}
              />
              <Route
                path="/order/success"
                element={<Order status="sending" />}
              />
              <Route
                path="/order/salepage"
                element={<Order status="salepage" />}
              />

              {FlashTrackingStatus.map((v) => (
                <Route
                  key={v.c}
                  path={`/order/tracking/flash/${v.c}`}
                  element={
                    <Order code={v.c} mode={`flash`} status="tracking" />
                  }
                />
              ))}

              {JntTrackingStatus.map((v) => (
                <Route
                  key={v.c}
                  path={`/order/tracking/jnt/${v.c}`}
                  element={
                    <Order
                      code={v.c}
                      mode={`jnt`}
                      statusTracking={v.code}
                      status="tracking"
                    />
                  }
                />
              ))}

              {KerryTrackingStatus.map((v) => (
                <Route
                  key={v.c}
                  path={`/order/tracking/kerry/${v.c}`}
                  element={
                    <Order
                      code={v.c}
                      mode={`kerry`}
                      statusTracking={v.code}
                      status="tracking"
                    />
                  }
                />
              ))}

              {ThaiPostTrackingStatus.map((v) => (
                <Route
                  key={v.c}
                  path={`/order/tracking/thaipost/${v.c}`}
                  element={
                    <Order
                      code={v.c}
                      mode={`thaipost`}
                      statusTracking={v.code}
                      status="tracking"
                    />
                  }
                />
              ))}

              {BestExpressTrackingStatus.map((v) => (
                <Route
                  key={v.c}
                  path={`/order/tracking/bestexpress/${v.c}`}
                  element={
                    <Order
                      code={v.c}
                      mode={`bestexpress`}
                      statusTracking={v.code}
                      status="tracking"
                    />
                  }
                />
              ))}

              {SpxExpressTrackingStatus.map((v) => (
                <Route
                  key={v.c}
                  path={`/order/tracking/spx/${v.c}`}
                  element={
                    <Order
                      code={v.c}
                      mode={`spx`}
                      statusTracking={v.code}
                      status="tracking"
                    />
                  }
                />
              ))}

              <Route path="/order/cancel" element={<Order status="cancel" />} />
              <Route path="/order/return" element={<Order status="return" />} />

              <Route path="/user" element={<User role="admin-emp" />} />
              <Route path="/user/create" element={<UserCreate />} />
              <Route path="/user/:id/edit" element={<UserCreate />} />
              <Route path="/user/:username/profile" element={<UserProfile />} />

              <Route path="/member" element={<User role="member" />} />

              <Route path="/report/monthly" element={<ReportMonthy />} />
              <Route path="/report/order" element={<ReportOrder />} />
              <Route path="/report/commission" element={<ReportCommission />} />
              <Route path="/report/customer" element={<ReportCustomer />} />
              <Route
                path="/report/lead-notification"
                element={<ReportLeadNotification />}
              />

              <Route path="/report/daily" element={<ReportDaily />} />
              <Route path="/report/product" element={<ReportProduct />} />

              <Route path="/flash-express/orders" element={<FlashOrder />} />

              <Route
                path="/flash-express/print"
                element={
                  <DomainAuth shipping="flash">
                    <FlashPrintHistory />
                  </DomainAuth>
                }
              />

              <Route path="/cod-checker" element={<CodChecker />} />

              {/*<Route*/}
              {/*  path="/flash-express/notifications"*/}
              {/*  element={*/}
              {/*    <DomainAuth shipping="flash">*/}
              {/*      <FlashExpressNotification />*/}
              {/*    </DomainAuth>*/}
              {/*  }*/}
              {/*/>*/}

              {/*<Route path="/flash-express/ka/cod-bills" element={<KACodBills/>}/>*/}
              {/*<Route*/}
              {/*  path="/flash-express/ka/cod"*/}
              {/*  element={*/}
              {/*    <DomainAuth shipping="flash">*/}
              {/*      <CodReport />*/}
              {/*    </DomainAuth>*/}
              {/*  }*/}
              {/*/>*/}
              {/*<Route*/}
              {/*  path="/flash-express/ka/pod"*/}
              {/*  element={*/}
              {/*    <DomainAuth shipping="flash">*/}
              {/*      <POD />*/}
              {/*    </DomainAuth>*/}
              {/*  }*/}
              {/*/>*/}

              <Route
                path="/jnt/orders"
                element={
                  <DomainAuth shipping="jnt">
                    <JntOrder />
                  </DomainAuth>
                }
              />
              <Route
                path="/laos/orders"
                element={
                  <DomainAuth shipping="laos">
                    <LaosOrders />
                  </DomainAuth>
                }
              />
              <Route
                path="/thaipost/orders"
                element={
                  <DomainAuth shipping="thaipost">
                    <ThaipostOrder />
                  </DomainAuth>
                }
              />

              <Route
                path="/kerry/orders"
                element={
                  <DomainAuth shipping="kerry">
                    <KerryOrder />
                  </DomainAuth>
                }
              />

              <Route
                path="/bestexpress/orders"
                element={
                  <DomainAuth shipping="bestexpress">
                    <BestExpressOrder />
                  </DomainAuth>
                }
              />
              <Route
                path="/spx/orders"
                element={
                  // <DomainAuth shipping="bestexpress">
                  <SpxOrder />
                  // </DomainAuth>
                }
              />

              <Route path="/print-history" element={<FlashPrintHistory />} />

              <Route exact path="/address" element={<Address />} />

              <Route
                exact
                path="/setting/general"
                element={<SettingGeneral />}
              />
              <Route
                exact
                path="/setting/address/kerry"
                element={<AddressKerry />}
              />
              <Route
                exact
                path="/setting/address/thaipost"
                element={<AddressThaipost />}
              />
              <Route exact path="/setting/channel" element={<Channels />} />
              {/*<Route path="/flash-express/printed" element={<ReportShipping type="printed"/>}/>*/}
              {/*<Route path="/flash-express/print-wait" element={<ReportShipping type="print-wait"/>}/>*/}
            </Route>
            <Route exact path="/denied" element={<PageDenied />} />
          </Routes>
        </React.Suspense>
      </Router>
      <ReactNotifications />
    </>
  );
}

export default App;
